import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  Grid,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const theme = useTheme();

  const sectionHeaderSx = {
    color: theme.palette.text.primary,
    mt: 5,
    mb: 2,
    fontWeight: 600,
  };

  const bodySx = {
    color: theme.palette.text.secondary,
    mb: 2,
    lineHeight: 1.8,
  };

  const stats = [
    { value: '270+', label: 'API Endpoints' },
    { value: '75+', label: 'Database Tables' },
    { value: '32', label: 'Platform Modules' },
    { value: '10', label: 'PDF Report Types' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            Home
          </Link>
          <Typography color="text.primary">About</Typography>
        </Breadcrumbs>

        <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: 700 }}>
          About AmudFin
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
          Operational infrastructure for Luxembourg fund management
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Mission */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          Our Mission
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin exists to modernise the operational backbone of Luxembourg&apos;s fund industry.
          Fund administrators, management companies, and compliance teams manage billions in assets
          using fragmented workflows — spreadsheets for NAV calculations, email chains for approval
          processes, and manual checks for regulatory compliance. This operational friction
          introduces risk, delays, and cost at every step.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We built AmudFin to replace that fragmentation with a single, purpose-built platform.
          Every workflow — from share class management to AML screening, from investor onboarding
          to CSSF reporting — runs through one system with full audit trails, four-eyes controls,
          and real-time compliance monitoring.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* What We Do */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          What We Do
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mt: 3, mb: 1, fontWeight: 600 }}>
          Fund Operations
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin handles the full lifecycle of fund operations. NAV calculations flow through a
          structured strike workflow — calculate, review, approve, publish — with SHA-256 hash
          chains ensuring data integrity at every stage. Share class management supports multiple
          pricing sources, dealing day configurations, and automated fee calculations. The
          transaction engine processes subscriptions, redemptions, and switches with settlement date
          calculation aligned to dealing calendars. The investor register maintains complete KYC
          profiles, beneficial ownership structures, and position tracking across all share classes.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mt: 3, mb: 1, fontWeight: 600 }}>
          Compliance and Regulatory Reporting
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Regulatory compliance is built into every layer of the platform, not bolted on as an
          afterthought. AML screening integrates with sanctions lists and adverse media databases.
          The compliance monitoring engine tracks obligations under UCITS, AIFMD, SFDR, PRIIPs,
          and Luxembourg-specific CSSF circulars. KID (Key Information Document) management
          generates and maintains documents required under PRIIPs Regulation (EU) 1286/2014.
          Data retention policies enforce CSSF-mandated retention periods automatically, with
          configurable rules per data category.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.text.primary, mt: 3, mb: 1, fontWeight: 600 }}>
          AI-Assisted Analytics
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Our predictive compliance engine analyses fund data, transaction patterns, and regulatory
          filings to surface potential compliance issues before they become violations. Risk
          distribution analysis provides portfolio-level views of concentration risk, counterparty
          exposure, and regulatory limit utilisation. Cash projection modelling forecasts liquidity
          needs based on historical patterns and upcoming dealing dates. These tools augment human
          expertise — every AI-generated insight passes through human review and the four-eyes
          approval workflow before any action is taken.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Platform Numbers */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          Platform at a Glance
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat) => (
            <Grid item xs={6} sm={3} key={stat.label}>
              <Card
                sx={{
                  bgcolor: theme.palette.background.paper,
                  textAlign: 'center',
                  py: 2,
                }}
                variant="outlined"
              >
                <CardContent>
                  <Typography
                    variant="h4"
                    sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 0.5 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Why Luxembourg */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          Why Luxembourg
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Luxembourg is the largest fund domicile in Europe and the second largest in the world,
          with over EUR 5 trillion in assets under management across more than 14,000 investment
          fund entities. The Grand Duchy&apos;s regulatory framework — anchored by the CSSF and
          aligned with EU directives — sets a high standard for investor protection, operational
          governance, and cross-border distribution.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin is purpose-built for this ecosystem. Our platform natively supports the regulatory
          requirements that Luxembourg fund operators face daily: CSSF circular compliance, UCITS
          investment restriction monitoring, AML obligations under the Law of 12 November 2004, and
          the specific reporting formats required by Luxembourg authorities. By building from
          Luxembourg outward, we ensure that the most demanding regulatory requirements are met
          first, creating a foundation that extends naturally to other EU jurisdictions.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Team */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          Our Team
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Our team brings together deep expertise in Luxembourg fund administration, financial
          regulation, and enterprise software engineering. With combined experience spanning fund
          accounting, transfer agency operations, CSSF regulatory reporting, and large-scale
          distributed systems, we understand the operational realities of fund management from the
          inside.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We work closely with fund administrators, management companies, and compliance
          professionals to ensure that every feature we build solves a real operational problem.
          Our development process is driven by direct feedback from practitioners who use the
          platform daily.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Technology */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          Technology
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin is built on a modern, production-grade technology stack designed for security,
          reliability, and performance:
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { name: 'FastAPI (Python)', desc: 'High-performance async backend with automatic OpenAPI documentation' },
            { name: 'React with TypeScript', desc: 'Type-safe frontend with Material-UI component library' },
            { name: 'PostgreSQL', desc: 'Enterprise-grade relational database with ACID compliance and row-level security' },
            { name: 'Docker', desc: 'Containerised deployment for consistent, reproducible environments' },
            { name: 'RS256 JWT Authentication', desc: 'Asymmetric key authentication with mandatory two-factor verification' },
            { name: 'SHA-256 Hash Chains', desc: 'Cryptographic audit trails for NAV data integrity verification' },
          ].map((tech) => (
            <Grid item xs={12} sm={6} key={tech.name}>
              <Card
                sx={{ bgcolor: theme.palette.background.paper, height: '100%' }}
                variant="outlined"
              >
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, mb: 0.5 }}>
                    {tech.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {tech.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Contact */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          Contact Us
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>AmudFin S.A.</strong><br />
          9, Boulevard Prince Henri<br />
          L-1724 Luxembourg<br />
          Grand Duchy of Luxembourg
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          General enquiries: contact@amudfin.com<br />
          Sales: sales@amudfin.com<br />
          Support: support@amudfin.com<br />
          Security: security@amudfin.com
        </Typography>

        <Box sx={{ mt: 4, mb: 4 }} />
      </Container>
    </Box>
  );
};

export default AboutPage;
